import { Route, Switch } from 'wouter';
import { SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { DashboardPage } from './pages/dashboard';
import { Home } from './pages/home';
import { CustomersPage } from './pages/customers';
import { VendorsPage } from './pages/vendors';
import { VendorInboxPage } from './pages/vendor-inbox';
import { MatrixPage } from './pages/matrix';
import { CoverageGapsPage } from './pages/coverage-gaps';
import { BillingPage } from './pages/billing';
import { PartsBaseAutomationPage } from '@/pages/partsbase-automation';
import { CompliancePage } from '@/pages/compliance';
import { OrdersPage } from './pages/orders';
import { AltPartSearchPage } from './pages/alt-part-search';
import { CatalogReviewPage } from './pages/catalog-review';
import { CopilotSettingsPage } from './pages/copilot-settings';
import { SignInPage } from './pages/sign-in';

// Wrap a page so it requires Clerk sign-in. Reduces repetition in the route table.
function Protected({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

export function App() {
  return (
    <Switch>
      {/* Clerk's <SignIn routing="path"> drives multi-step auth by navigating
       *  to sub-paths — /sign-in/factor-one (password/code), /sign-in/factor-two
       *  (2FA), /sign-in/sso-callback, /sign-in/reset-password, etc. Register the
       *  bare path AND the sub-path wildcard, or those steps fall through to the
       *  catch-all → RedirectToSignIn → loop back to the email screen. wouter's
       *  :rest* does NOT match the bare URL, so both forms are required. */}
      <Route path="/sign-in" component={SignInPage} />
      <Route path="/sign-in/:rest*" component={SignInPage} />

      {/* Vendors — list + detail.
       *  wouter's `:rest*` quantifier does NOT match the bare `/vendors` URL
       *  (no trailing slash), so we register both forms explicitly. */}
      <Route path="/vendors">
        <Protected>
          <VendorsPage />
        </Protected>
      </Route>
      <Route path="/vendors/:publicId">
        <Protected>
          <VendorsPage />
        </Protected>
      </Route>

      {/* RFQ console — list + detail */}
      <Route path="/rfqs">
        <Protected>
          <Home />
        </Protected>
      </Route>
      <Route path="/rfqs/:publicId">
        <Protected>
          <Home />
        </Protected>
      </Route>

      {/* Orders — active fulfillment view */}
      <Route path="/orders">
        <Protected>
          <OrdersPage />
        </Protected>
      </Route>

      {/* Customers — list + scorecard detail */}
      <Route path="/customers">
        <Protected>
          <CustomersPage />
        </Protected>
      </Route>
      <Route path="/customers/:publicId">
        <Protected>
          <CustomersPage />
        </Protected>
      </Route>

      {/* Vendor inbox — quotes@ email triage (list + detail) */}
      <Route path="/vendor-inbox">
        <Protected>
          <VendorInboxPage />
        </Protected>
      </Route>
      <Route path="/vendor-inbox/:publicId">
        <Protected>
          <VendorInboxPage />
        </Protected>
      </Route>

      {/* Matrix grid */}
      <Route path="/matrix">
        <Protected>
          <MatrixPage />
        </Protected>
      </Route>

      {/* Alt-part search — standalone NSN cross-reference lookup */}
      <Route path="/alt-parts">
        <Protected>
          <AltPartSearchPage />
        </Protected>
      </Route>

      {/* Catalog review (Part Intelligence Phase 1C) — human review, never edits the catalog */}
      <Route path="/catalog-review">
        <Protected>
          <CatalogReviewPage />
        </Protected>
      </Route>

      {/* APD Copilot: mailboxes, sharing, model (pilot tenants only) */}
      <Route path="/copilot/settings">
        <Protected>
          <CopilotSettingsPage />
        </Protected>
      </Route>

      {/* Coverage gaps */}
      <Route path="/coverage-gaps">
        <Protected>
          <CoverageGapsPage />
        </Protected>
      </Route>

      <Route path="/partsbase-automation">
        <Protected>
          <PartsBaseAutomationPage />
        </Protected>
      </Route>

      <Route path="/compliance">
        <Protected>
          <CompliancePage />
        </Protected>
      </Route>

      <Route path="/billing">
        <Protected>
          <BillingPage />
        </Protected>
      </Route>

      {/* Dashboard — default */}
      <Route>
        <Protected>
          <DashboardPage />
        </Protected>
      </Route>
    </Switch>
  );
}
